<template>
  <div>
    <div class="row g-4">
      <div class="col">
        <router-link to="/work/buckets" class="btn btn-primary btn-no-r">
          <span><BaseIcon name="active" /> {{ $t("work.dashboard") }}</span>
        </router-link>
        <router-link
          to="/work/tracking"
          class="btn btn-no-l btn-no-r btn-secondary"
        >
          <span><BaseIcon name="timer" /> {{ $t("work.timeTracking") }}</span>
        </router-link>
        <router-link
          to="/work/tracking/performance"
          class="btn btn-no-l btn-secondary"
        >
          <span><BaseIcon name="timer" /> {{ $t("work.timeTracking") }}</span>
        </router-link>
      </div>
      <div class="col-auto">
        <v-select
          v-model="selectedFilter"
          :options="filterOptions"
          label="name"
          placeholder="Filter by bucket"
          :clearable="true"
          @update:modelValue="filterBuckets"
        />
      </div>
      <div class="col-auto">
        <button class="btn btn-outline-primary" @click="showNewBucketModal">
          <span><BaseIcon name="plus" /> {{ $t("work.addBucket") }}</span>
        </button>
      </div>
      <!-- ====== buckets Loop ====== -->
      <div v-for="bucket in filteredItems" :key="bucket.id" class="col-12">
        <div class="card p-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="fw-bold">{{ bucket.name }}</div>
            <button
              class="btn btn-sm btn-outline-primary"
              @click="addGroup(bucket)"
            >
              + Add Group
            </button>
          </div>

          <!-- Task groups -->
          <div
            v-for="group in bucket.workTaskGroups"
            :key="group.id"
            class="mb-4"
          >
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h3 class="small text-uppercase text-muted mb-0">
                {{ group.name }}
              </h3>
              <div>
                <button
                  class="btn btn-sm btn-link p-0 text-decoration-none me-2"
                  @click="connectGroupToCRM(group)"
                >
                  <span v-if="group.crmPartner">{{
                    group.crmPartner?.name
                  }}</span>
                  <span v-else>CRM</span>
                </button>
                <button
                  class="btn btn-sm btn-link p-0 text-decoration-none"
                  @click="addTask(group)"
                >
                  + Add Task
                </button>
              </div>
            </div>
            <template v-if="group.workTasks?.length > 0">
              <div class="timeline-header">
                <div>Name</div>
                <div>Status</div>
                <div>Priority</div>
                <div>Assigned</div>
                <div
                  v-for="n in days"
                  :key="bucket.name + '-header-' + n"
                  class="text-center"
                >
                  {{ getDay(n) }}
                </div>
              </div>
              <div
                v-for="task in group.workTasks"
                :key="task.id"
                class="gantt-row"
              >
                <div
                  @click="showTaskModal(task)"
                  style="cursor: pointer; width: 200px"
                  class="text-truncate"
                >
                  {{ task.name }}
                </div>

                <div>
                  <div
                    v-if="!task.editStatus"
                    @click="task.editStatus = true"
                    class="cursor-pointer"
                  >
                    <span
                      v-if="statusOptions.find((o) => o.id === task.status)"
                      :class="
                        statusOptions.find((o) => o.id === task.status).color
                      "
                      class="badge"
                    >
                      {{ statusOptions.find((o) => o.id === task.status).name }}
                    </span>
                    <span v-else class="badge bg-light text-dark"
                      >Select Status</span
                    >
                  </div>
                  <v-select
                    v-else
                    v-model="task.status"
                    :options="statusOptions"
                    :reduce="(opt) => opt.id"
                    label="name"
                    class="v-select-sm"
                    :clearable="false"
                    :autofocus="true"
                    @update:modelValue="
                      quickUpdateTask(task, 'status');
                      task.editStatus = false;
                    "
                    @search:blur="task.editStatus = false"
                  >
                    <template #selected-option="option">
                      <span :class="option.color" class="badge">
                        {{ option.name }}
                      </span>
                    </template>
                  </v-select>
                </div>

                <div>
                  <div
                    v-if="!task.editPriority"
                    @click="task.editPriority = true"
                    class="cursor-pointer"
                  >
                    <span
                      v-if="priorityOptions.find((o) => o.id === task.priority)"
                      :class="
                        priorityOptions.find((o) => o.id === task.priority)
                          .color
                      "
                      class="badge"
                    >
                      {{
                        priorityOptions.find((o) => o.id === task.priority).name
                      }}
                    </span>
                    <span v-else class="badge bg-light text-dark"
                      >Select Priority</span
                    >
                  </div>
                  <v-select
                    v-else
                    v-model="task.priority"
                    :options="priorityOptions"
                    :reduce="(opt) => opt.id"
                    label="name"
                    class="v-select-sm"
                    :clearable="false"
                    :autofocus="true"
                    @update:modelValue="
                      quickUpdateTask(task, 'priority');
                      task.editPriority = false;
                    "
                    @search:blur="task.editPriority = false"
                  >
                    <template #selected-option="option">
                      <span :class="option.color" class="badge">
                        {{ option.name }}
                      </span>
                    </template>
                  </v-select>
                </div>

                <div>
                  <div
                    v-if="!task.editUser"
                    @click="task.editUser = true"
                    class="cursor-pointer"
                  >
                    <span
                      v-if="users.find((u) => u.id === task.user_id)"
                      class="badge bg-light text-dark border"
                    >
                      {{ users.find((u) => u.id === task.user_id).full_name }}
                    </span>
                    <span v-else class="badge bg-light text-dark border">
                      Unassigned
                    </span>
                  </div>
                  <v-select
                    v-else
                    v-model="task.user_id"
                    :options="users"
                    :reduce="(opt) => opt.id"
                    label="full_name"
                    class="v-select-sm"
                    :autofocus="true"
                    :searchable="true"
                    @update:modelValue="
                      quickUpdateTask(task, 'user_id');
                      task.editUser = false;
                    "
                    @search:blur="task.editUser = false"
                  />
                </div>

                <div
                  v-if="calculateTaskPlan(task)"
                  class="plan-bar"
                  :class="task.planColor"
                  :style="{
                    gridColumn:
                      calculateTaskPlan(task) +
                      ' / span ' +
                      calculateTaskSpan(task),
                    width: '100%',
                  }"
                >
                  <div
                    class="actual-bar"
                    :style="{ width: `${task.finished_percentage}%` }"
                  ></div>
                </div>
              </div>
            </template>
            <template v-else>
              <Vue3Lottie
                :animationData="emptyJSON"
                :height="64"
                :width="420"
              />
            </template>
          </div>

          <!-- Heatmaps -->
          <div class="small text-uppercase text-muted mt-3">Daily Tracking</div>

          <div
            v-for="user in bucket.times"
            :key="user.user.id"
            class="user-heatmap row"
          >
            <div
              class="fw-bold col-2 cursor-pointer"
              @click="showUserTrackings(user.user, bucket.id)"
            >
              {{ user.user.first_name }} {{ user.user.last_name }}
            </div>

            <div
              class="heatmap-grid col-10"
              :style="{
                gridTemplateColumns: 'repeat(28,1fr)',
              }"
            >
              <div
                v-for="(value, index) in user.times"
                :key="user.id + '-' + index"
                class="heat-cell cursor-pointer"
                :class="'level-' + calculateTimeLevel(value)"
                @click="showUserTrackings(user.user, bucket.id, index)"
              >
                <div class="day-label">{{ getDay(index) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <NewBucket ref="addModal" v-on:add="addNewRow" :statuses="statuses" />
    <WorkTaskModal ref="taskModal" :users="users" @updated="onTaskUpdated" />
    <PromptModal
      ref="promptModal"
      :title="promptTitle"
      :label="promptLabel"
      @success="onPromptSuccess"
    />

    <UserTrackingsModal
      ref="trackingsModal"
      :show="showTrackingsModal"
      :user="selectedUser"
      :bucket-id="selectedBucketId"
      @close="showTrackingsModal = false"
    />
    <ConnectGroupToCRM
      ref="connectCRMModal"
      :group="BucketGroupSettings"
      @connected="onCRMConnected"
    />
  </div>
</template>

<script>
import { useStore } from "vuex";
import ws from "@/modules/ws";
import http from "@/modules/http";
import lineOptions from "@/modules/lineOptions";
import NewBucket from "../../../components/modals/NewBucket.vue";
import WorkTaskModal from "@/components/modals/WorkTaskModal.vue";
import PromptModal from "@/components/modals/PromptModal.vue";
import ConnectGroupToCRM from "@/components/modals/ConnectGroupToCRM.vue";
import UserTrackingsModal from "@/components/modals/UserTrackingsModal.vue";
import WorkTaskStatuses from "@/modules/WorkTaskStatuses";
import WorkTaskPriorities from "@/modules/WorkTaskPriorities";
import { Vue3Lottie } from "vue3-lottie";
import TumbleweedRolling from "../../../assets/animations/TumbleweedRolling.json";
import BaseIcon from "../../../components/icons/BaseIcon.vue";
export default {
  name: "Operations",
  components: {
    BaseIcon,
    NewBucket,
    WorkTaskModal,
    PromptModal,
    ConnectGroupToCRM,
    UserTrackingsModal,
    Vue3Lottie,
  },
  created() {},
  data() {
    return {
      store: useStore(),
      tableKey: 0,
      rows: [],
      emptyJSON: TumbleweedRolling,
      filtersTimeOut: null,
      selectedLineOption: 0,
      lineOptions: [],
      selectAll: false,
      items: [],
      statuses: [],
      users: [],
      statusOptions: Object.values(WorkTaskStatuses),
      priorityOptions: Object.values(WorkTaskPriorities),
      selectedItems: [],
      selectedEditMethod: {},
      page: 1,
      newCustomRowValue: undefined,
      listOptions: [],
      promptTitle: "",
      promptLabel: "",
      promptCallback: null,
      BucketGroupSettings: null,
      partners: [],
      selectedPartner: null,
      showTrackingsModal: false,
      selectedUser: null,
      selectedBucketId: null,
      selectedDate: null,
      selectedFilter: null,
      filterOptions: [],
      order: {
        row: null,
        desc: true,
      },
    };
  },
  computed: {
    filteredItems() {
      if (!this.selectedFilter) {
        return this.items;
      }
      return this.items.filter(item => item.id === this.selectedFilter.id);
    },
    days() {
      const days = [];
      const today = new Date();

      for (let i = 0; i < 28; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        days.push(`${year}-${month}-${day}`);
      }

      return days;
    },
    warehouses() {
      return this.store.state.warehouses;
    },
    path() {
      return this.$route.path;
    },
    sessionId() {
      return this.store.state.sessionId;
    },
    filters() {
      return this.store.state.filters?.buckets;
    },
  },
  mounted() {
    this.$store.commit("setFilterPage", "buckets");
    this.loadData();
    this.loadUsers();

    ws.init();

    ws.subscribe("add", "buckets", (e) => {
      if (e.session !== this.sessionId) {
        this.items.push(e.object);
        this.tableKey++;
      }
    });

    ws.subscribe("modify", "buckets", (e) => {
      let obj = this.items[this.items.findIndex((o) => o.id === e.objectId)];
      if (obj !== undefined) {
        if (e.session !== this.sessionId) {
          obj[e.rowKey] = e.value;
        }
      }
    });
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        if (this.filtersTimeOut !== null) {
          clearTimeout(this.filtersTimeOut);
        }
        this.filtersTimeOut = setTimeout(() => {
          this.filtersTimeOut = null;
          this.items = undefined;
          this.page = 1;
          this.loadData();
        }, 300);
      },
    },
  },
  methods: {
    filterBuckets() {
      // Filtering is handled by computed property filteredItems
    },
    orderBy(order) {
      this.items = undefined;
      this.page = 1;
      this.order = order;
      this.loadData();
    },
    loadUsers() {
      http.fetch("/users").then((data) => {
        this.users = data.map((u) => ({
          ...u,
          full_name: `${u.first_name} ${u.last_name}`,
        }));
      });
    },
    showNewBucketModal() {
      this.$refs.addModal.showModal();
    },
    addGroup(bucket) {
      this.promptTitle = "Add Group";
      this.promptLabel = "Group Name";
      this.promptCallback = (name) => {
        http
          .fetch(
            "/work/task-groups",
            { work_bucket_id: bucket.id, name },
            true,
            "POST"
          )
          .then((res) => {
            if (res.success) {
              if (!bucket.workTaskGroups) bucket.workTaskGroups = [];
              bucket.workTaskGroups.push({ ...res.group, workTasks: [] });
            }
          });
      };
      this.$refs.promptModal.showModal();
    },
    connectGroupToCRM(group) {
      console.log("Attempting to connect group to CRM", group);
      this.BucketGroupSettings = group;
      // Load CRM partners for the modal
      http.fetch("/crm/partners").then((data) => {
        this.partners = data.data;
        if (this.$refs.connectCRMModal) {
          this.$refs.connectCRMModal.showModal();
        } else {
          console.error("connectCRMModal reference not found");
        }
      });
    },
    onCRMConnected(partner) {
      if (this.BucketGroupSettings) {
        this.BucketGroupSettings.crmPartner = partner;
        this.BucketGroupSettings = null;
      }
    },
    addTask(group) {
      this.promptTitle = "Add Task";
      this.promptLabel = "Task Name";
      this.promptCallback = (name) => {
        http
          .fetch(
            "/work/tasks",
            { work_task_group_id: group.id, name },
            true,
            "POST"
          )
          .then((res) => {
            if (res.success) {
              if (!group.workTasks) group.workTasks = [];
              group.workTasks.push(res.task);
            }
          });
      };
      this.$refs.promptModal.showModal();
    },
    onPromptSuccess(value) {
      if (this.promptCallback) {
        this.promptCallback(value);
      }
    },
    quickUpdateTask(task, field) {
      const data = {};
      data[field] = task[field];
      http.fetch(`/work/tasks/${task.id}`, data, true, "PUT");
    },
    showTaskModal(task) {
      this.$refs.taskModal.show(task);
    },
    onTaskUpdated(updatedTask) {
      // Find the task in our local state and update it
      for (const bucket of this.items) {
        if (!bucket.workTaskGroups) continue;
        for (const group of bucket.workTaskGroups) {
          if (!group.workTasks) continue;
          const index = group.workTasks.findIndex(
            (t) => t.id === updatedTask.id
          );
          if (index !== -1) {
            group.workTasks[index] = updatedTask;
            return;
          }
        }
      }
    },
    loadMore() {
      console.log("No more page yet");
      // this.page++;
      // this.loadData();
    },
    addNewRow(row) {
      row.selected = this.selectAll;
      if (this.selectAll) {
        this.selectAllDiff++;
      }
      this.items.unshift(row);
      this.tableKey++;
    },
    loadData() {
      let url = `/work/buckets?page=${this.page}&order[desc]=${
        this.order.desc ? 1 : 0
      }&order[row]=${this.order.row ? this.order.row : ""}`;

      if (Object.keys(this.filters).length > 0) {
        for (const filter in this.filters) {
          if (Array.isArray(this.filters[filter].value)) {
            for (const value of this.filters[filter].value) {
              url += `&filter[${filter}][]=${value}`;
            }
          } else {
            url += `&filter[${filter}]=${this.filters[filter].value}`;
          }
        }
      }
      http.fetch(url).then((data) => {
        if (this.page === 1) {
          if (this.rows.length === 0) {
            this.rows = lineOptions.init(data.rows, data.line_options);
            this.lineOptions = data.line_options;
            this.selectedLineOption = data.line_options[0]?.id;
          }
          this.statuses = data.statuses;
          this.items = data.data;
          this.filterOptions = this.items.map(b => ({ id: b.id, name: b.name }));
          this.tableKey++;
        } else {
          for (const row of data.data) {
            this.items.push(row);
          }
        }
      });
    },
    valUpdated(val, row, id, item) {
      if (this.selectedItems.length > 1) {
        this.$refs.multipleEditModal.showModal();
        this.selectedEditMethod = {
          val: val,
          row: row,
          id: id,
          item: item,
        };
      }
      this.editItems(val, row, id, item);
    },
    multipleEdit() {
      this.editItems(
        this.selectedEditMethod.val,
        this.selectedEditMethod.row,
        this.selectedItems.map((e) => e.id),
        this.selectedEditMethod.item
      );
    },
    editItems(val, row, id, item) {
      let data = { rows: id };
      data[row] = val;

      if (typeof val === "object" && val.length === 0) {
        val.push({ id: 0 });
      }

      if (val === 0 && this.newCustomRowValue === undefined) {
        return;
      }

      if (val === 0 && this.newCustomRowValue !== undefined) {
        data[row] = this.newCustomRowValue;
        data.add = 1;
        this.newCustomRowValue = undefined;
      }

      if (row === "cost") {
        data["cost"] = val.amount;
        data["cost_currency_code"] = val.currency;
      }

      http.fetch(`/work/buckets`, data, true, "PUT").then((respond) => {
        if (data.add) {
          let rowData = this.rows.filter((e) => e.key === row)[0];
          rowData.values.push({ id: respond.id, name: data[row] });
          item[row] = respond.id;
          this.newCustomRowValue = undefined;
        }
      });
    },
    calculateTimeLevel(duration) {
      return Math.ceil(duration / 3600);
    },
    getDay(date) {
      const d = new Date(date);
      const day = d.getDate();
      return day.toString().padStart(2, "0");
    },
    showUserTrackings(user, bucketId, date = null) {
      this.selectedUser = user;
      this.selectedBucketId = bucketId;
      this.selectedDate = date;
      this.showTrackingsModal = true;

      this.$nextTick(() => {
        if (this.$refs.trackingsModal) {
          const dayDate = date ? new Date(date) : null;
          this.$refs.trackingsModal.loadWithFilters(user.id, bucketId, dayDate);
        }
      });
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const d = new Date(dateString);
      return d.toLocaleString();
    },
    formatDuration(seconds) {
      if (!seconds) return "0s";
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      if (minutes > 0) {
        return `${minutes}m ${secs}s`;
      }
      return `${secs}s`;
    },
    calculateTaskPlan(task) {
      if (task.started_at === null) {
        return 0;
      }
      const start = new Date(task.started_at);
      const duration = task.day_durations ?? 1;

      const taskStart = new Date(start);
      const taskEnd = new Date(start);
      taskEnd.setDate(taskEnd.getDate() + duration - 1);

      const timelineStart = new Date(this.days[0]);
      const timelineEnd = new Date(this.days[this.days.length - 1]);

      // Task completely outside visible timeline
      if (taskEnd < timelineStart || taskStart > timelineEnd) {
        return null;
      }

      // Clamp start to timeline
      const visibleStart =
        taskStart < timelineStart ? timelineStart : taskStart;

      const date = visibleStart.toISOString().slice(0, 10);
      const startIndex = this.days.indexOf(date);

      return startIndex + 5;
    },
    calculateTaskSpan(task) {
      if (task.started_at === null) {
        return 0;
      }
      const start = new Date(task.started_at);
      const duration = task.day_durations ?? 1;

      const taskStart = new Date(start);
      const taskEnd = new Date(start);
      taskEnd.setDate(taskEnd.getDate() + duration - 1);

      const timelineStart = new Date(this.days[0]);
      const timelineEnd = new Date(this.days[this.days.length - 1]);

      if (taskEnd < timelineStart || taskStart > timelineEnd) {
        return 0;
      }

      const visibleStart =
        taskStart < timelineStart ? timelineStart : taskStart;
      const visibleEnd = taskEnd > timelineEnd ? timelineEnd : taskEnd;

      const diff = (visibleEnd - visibleStart) / (1000 * 60 * 60 * 24) + 1;

      return Math.floor(diff);
    },
    deleteBucket() {
      let data = [];
      for (const item of this.selectedItems) {
        data.push(item.id);
      }
      http.fetch(`/work/buckets`, { rows: data }, true, "DELETE").then(() => {
        for (const item of this.selectedItems) {
          let i = this.items.indexOf(item);
          if (i > -1) {
            this.items.splice(i, 1);
          }
        }
        this.tableKey++;
      });
    },
  },
};
</script>
<style scoped>
.card {
  border: none;
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
}

.timeline-header {
  display: grid;
  grid-template-columns: 180px 120px 120px 120px repeat(28, 1fr);
  font-size: 0.7rem;
  color: #9ca3af;
  margin-bottom: 10px;
}

.gantt-row {
  display: grid;
  grid-template-columns: 180px 120px 120px 120px repeat(28, 1fr);
  align-items: center;
  margin-bottom: 12px;
  position: relative;
}

.plan-bar {
  position: absolute;
  height: 18px;
  border-radius: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: -webkit-linear-gradient(#b39ee1, #bc8cd3);
}

.actual-bar {
  height: 18px;
  border-radius: 10px;
  background: -webkit-linear-gradient(#351c41, #26142f);
}

.user-heatmap {
  margin-top: 12px;
}

.heatmap-grid {
  display: grid;
  grid-template-columns: repeat(28, 1fr);
  gap: 4px;
}

.heat-cell {
  padding-top: 100%;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  transition: 0.2s;
}

.heat-cell:hover {
  transform: scale(1.1);
}

.level-0 {
  background: #e5e7eb;
}
.level-1 {
  background: #d8bae6;
}
.level-2 {
  background: #c89adf;
}
.level-3 {
  background: #b460dc;
}
.level-4 {
  background: #8548a3;
  color: #fff;
}
.level-5 {
  background: #632c7e;
  color: #fff;
}
.level-6 {
  background: #4f136d;
  color: #fff;
}
.level-7 {
  background: #351c41;
  color: #fff;
}
.level-8 {
  background: #1d0f23;
  color: #fff;
}

.day-label {
  position: absolute;
  top: 3px;
  left: 4px;
  font-size: 0.55rem;
}
.gantt-row > div:nth-child(2),
.gantt-row > div:nth-child(3),
.gantt-row > div:nth-child(4) {
  display: flex;
  align-items: center;
  padding: 0 5px;
}

.v-select-sm .vs__dropdown-toggle {
  padding: 0;
  min-height: 25px;
}
.v-select-sm .vs__selected {
  margin: 0;
  padding: 0;
}
.v-select-sm .vs__search {
  padding: 0;
  margin: 0;
}
</style>
